import React from "react";
import { Droplets, Recycle, Battery, Factory } from "lucide-react";
import { GlassCard } from "./GlassCard";

const Eyebrow = ({ children }) => (
    <span className="section-label">{children}</span>
);

const SectionTitle = ({ eyebrow, title, subtitle, align = "left" }) => (
    <div
        className={`max-w-3xl ${
            align === "center" ? "mx-auto text-center" : ""
        }`}
    >
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2 className="mt-4 font-display font-black text-ocean text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
            {title}
        </h2>
        {subtitle && (
            <p className="mt-5 text-inkblue text-base md:text-lg leading-relaxed">
                {subtitle}
            </p>
        )}
    </div>
);

// ------------- PROBLEM (merged into OpeningStatement) -------------

// ------------- PARADIGM -------------
export const Paradigm = () => (
    <section
        data-testid="paradigm-section"
        className="relative overflow-hidden bg-ocean-deep grain py-28 md:py-40 px-6 md:px-10"
    >
        <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
        >
            <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] rounded-full opacity-30 animate-blob-drift"
                style={{
                    background:
                        "radial-gradient(circle, #138A72 0%, rgba(19,138,114,0) 60%)",
                    filter: "blur(90px)",
                }}
            />
        </div>
        <div className="relative max-w-5xl mx-auto text-center">
            <Eyebrow>The Paradigm Shift</Eyebrow>
            <h2 className="mt-6 font-display font-black text-white text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tighter">
                Desalination doesn't have to{" "}
                <span className="text-white/40">cost</span> energy.
                <br />
                It can{" "}
                <span className="text-energy italic">generate</span> it.
            </h2>
            <p className="mt-8 text-white/70 text-base md:text-lg max-w-2xl mx-auto">
                Our system transforms industrial waste into clean water and
                electricity through a revolutionary circular process. No energy
                input required - industrial waste streams become fuel at
                ambient conditions.
            </p>
        </div>
    </section>
);

// ------------- PROCESS -------------
export const Process = () => {
    const steps = [
        {
            icon: Factory,
            n: "01",
            title: "Feed in waste",
            body: "Industrial acid and alkali waste streams are pumped into outer chambers. No energy input, no feedstock cost.",
        },
        {
            icon: Droplets,
            n: "02",
            title: "Deionize seawater",
            body: "Seawater flows through the central chamber. Ion-selective membranes pull salt ions out without high-pressure pumps.",
        },
        {
            icon: Battery,
            n: "03",
            title: "Harvest energy",
            body: "Neutralization energy from acid / alkali is harvested as electricity, producing drinking-grade freshwater simultaneously.",
        },
        {
            icon: Recycle,
            n: "04",
            title: "Close the loop",
            body: "The system runs at ambient temperature and pressure. Acid/alkali waste drives deionization in a fully circular process.",
        },
    ];
    return (
        <section
            id="technology"
            data-testid="technology-section"
            className="relative py-24 md:py-32 px-6 md:px-10 bg-lavender"
        >
            <div className="max-w-7xl mx-auto">
                <SectionTitle
                    eyebrow="How CPSD Works"
                    title="From waste to water to electricity."
                    subtitle="Climate-Positive Seawater Deionizer (CPSD): four steps, one circular flow."
                />
                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 relative">
                    {steps.map((s, i) => (
                        <GlassCard
                            key={s.n}
                            className="p-7 relative"
                            data-testid={`process-step-${i}`}
                        >
                            <div className="flex items-start justify-between">
                                <div className="w-12 h-12 rounded-xl bg-teal/15 text-teal flex items-center justify-center">
                                    <s.icon size={22} />
                                </div>
                                <span className="font-display font-black text-ocean/10 text-5xl leading-none">
                                    {s.n}
                                </span>
                            </div>
                            <h3 className="mt-6 font-display font-bold text-ocean text-xl tracking-tight">
                                {s.title}
                            </h3>
                            <p className="mt-3 text-inkblue text-sm leading-relaxed">
                                {s.body}
                            </p>
                        </GlassCard>
                    ))}
                </div>
            </div>
        </section>
    );
};

// ------------- CATEGORY + MARKET (merged) -------------
export const Market = () => (
    <section
        id="market"
        data-testid="market-section"
        className="relative overflow-hidden py-24 md:py-32 px-6 md:px-10 bg-ocean-deep grain"
    >
        <div
            className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[70vw] h-[70vw] rounded-full opacity-30 pointer-events-none"
            style={{
                background:
                    "radial-gradient(circle, #138A72 0%, rgba(19,138,114,0) 60%)",
                filter: "blur(100px)",
            }}
        />
        <div className="relative max-w-7xl mx-auto">
            <h2 className="font-display font-black text-white text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tighter max-w-4xl">
                Industrial desalination will be a{" "}
                <span className="text-energy">$31&nbsp;billion</span> market by
                2030. We're disrupting it.
            </h2>
        </div>
    </section>
);
